import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import {
  CloudArrowUpIcon,
  XMarkIcon,
  FilmIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import { videosAPI } from '../services/videosApi';
import { getApiErrorMessage } from '../utils/apiError';
import usePublicConfig from '../hooks/usePublicConfig';

// Direct-to-S3 part upload tuning (Anshul, 2026-09-26). Part URLs are signed in batches, just
// in time — a presigned URL dies with the task-role credentials that signed it — so this is
// how many the client asks for at once, not how many it holds in flight.
const PARTS_PER_BATCH = 8;
const MAX_PARALLEL_PARTS = 4;
// Finding D, 2026-09-26: exponential backoff with jitter, capped at 30s, and enough attempts
// that a brief connection drop cannot sink a large upload — the previous 3 attempts at a flat
// 0.5s/1s backoff gave up inside two seconds, well within range of an ordinary Wi-Fi blip.
const PART_RETRY_LIMIT = 8;
const PART_RETRY_BASE_MS = 500;
const PART_RETRY_MAX_DELAY_MS = 30000;

// A part PUT that never got a response at all (a blocked CORS preflight, a dropped connection)
// and one that got a response with no readable ETag (the bucket's CORS rule exists but does
// not expose it) look identical from here — both mean the browser cannot complete a direct
// upload against this bucket — so both read as the same actionable message.
const CORS_ERROR_MESSAGE =
  'The storage bucket refused the upload. Its CORS rule may be missing.';

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Exponential backoff with full jitter (finding D): the delay before retry `attempt`
 * (1-indexed) is a random value in `[0, min(PART_RETRY_MAX_DELAY_MS, base * 2^(attempt-1))]`,
 * so a burst of parts retrying at once do not all hammer the bucket in lockstep, and the
 * ceiling keeps a very late attempt from waiting an unreasonable amount of time. */
function backoffDelayMs(attempt) {
  const cap = Math.min(PART_RETRY_MAX_DELAY_MS, PART_RETRY_BASE_MS * 2 ** (attempt - 1));
  return Math.random() * cap;
}

/** One part's byte range within `file`, half-open at `end` (the last part is short). */
function partRange(file, partNumber, partBytes) {
  const start = (partNumber - 1) * partBytes;
  const end = Math.min(start + partBytes, file.size);
  return { start, end };
}

/**
 * PUT one part's bytes to its presigned URL via a raw `XMLHttpRequest` — never `window.fetch`
 * (finding C, 2026-09-26): no browser gives `fetch` an upload-progress event at all, and
 * `XMLHttpRequest.upload.onprogress` is what lets `onProgress` report real bytes sent
 * WITHIN a single (up to 64 MiB) part, instead of the bar only moving once a whole part
 * finishes. Never the shared axios instance either: a presigned S3 URL rejects a request
 * carrying an `Authorization` header, which axios's own interceptor would add.
 *
 * Resolves with the ETag the bucket answered with, having reported the part's full size to
 * `onProgress` first (covers a response with no upload-progress events at all, e.g. a
 * same-tick synthetic response in a test). Never resolves with a missing ETag — that case
 * rejects as `CORS_ERROR_MESSAGE` instead, since a caller cannot complete the multipart
 * upload without every part's ETag. The rejected `Error` for a non-2xx response carries a
 * `status` field so a caller can single out a 403 (finding D).
 */
function putPart(url, blob, signal, onProgress) {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException('Upload canceled', 'AbortError'));
      return;
    }
    const xhr = new XMLHttpRequest();
    const onAbort = () => xhr.abort();
    const cleanup = () => signal.removeEventListener('abort', onAbort);

    xhr.open('PUT', url);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress(event.loaded);
    };
    xhr.onload = () => {
      cleanup();
      if (xhr.status < 200 || xhr.status >= 300) {
        const err = new Error(`Part upload was rejected (status ${xhr.status}).`);
        err.status = xhr.status;
        reject(err);
        return;
      }
      const etag = xhr.getResponseHeader('ETag') || xhr.getResponseHeader('etag');
      if (!etag) {
        reject(new Error(CORS_ERROR_MESSAGE));
        return;
      }
      onProgress(blob.size);
      resolve(etag);
    };
    xhr.onerror = () => {
      cleanup();
      reject(new Error(CORS_ERROR_MESSAGE));
    };
    xhr.onabort = () => {
      cleanup();
      reject(new DOMException('Upload canceled', 'AbortError'));
    };
    signal.addEventListener('abort', onAbort, { once: true });
    xhr.send(blob);
  });
}

/** A fresh presigned URL for exactly one part (finding D): a URL that expired, or whose
 * task-role credentials died mid-upload, fails the same way on every retry unless replaced. */
async function fetchFreshPartUrl(videoId, partNumber, signal) {
  const response = await videosAPI.getDirectUploadPartUrls(
    videoId,
    { part_numbers: [partNumber] },
    { signal }
  );
  return response.data.urls[String(partNumber)];
}

/**
 * `putPart`, retried up to `PART_RETRY_LIMIT` times with exponential backoff and jitter
 * (finding D) — a single dropped connection on part 40 of 400 must not fail the whole upload.
 * Refetches a fresh presigned URL before any retry that follows a 403 response, or before ANY
 * retry past the first failure: a dead task-role credential often surfaces here as the same
 * CORS-shaped network error `putPart` already cannot tell apart from a missing CORS rule, so
 * this does not wait for a readable 403 past the first attempt before assuming the URL itself
 * may be the problem. A cancellation (the shared `AbortController` firing) is never retried;
 * it propagates immediately.
 */
async function uploadOnePartWithRetry({ videoId, partNumber, url, blob, signal, onProgress }) {
  let currentUrl = url;
  let lastError;
  for (let attempt = 1; attempt <= PART_RETRY_LIMIT; attempt += 1) {
    if (signal.aborted) throw new DOMException('Upload canceled', 'AbortError');
    try {
      // eslint-disable-next-line no-await-in-loop
      return await putPart(currentUrl, blob, signal, onProgress);
    } catch (err) {
      if (signal.aborted) throw err;
      lastError = err;
      if (attempt < PART_RETRY_LIMIT) {
        if (err?.status === 403 || attempt > 1) {
          try {
            // eslint-disable-next-line no-await-in-loop
            currentUrl = await fetchFreshPartUrl(videoId, partNumber, signal);
          } catch (refetchErr) {
            if (signal.aborted) throw refetchErr;
            // The refetch itself may be a transient blip; keep retrying with the URL already
            // in hand rather than failing the whole part over a failed refetch attempt.
          }
        }
        // eslint-disable-next-line no-await-in-loop
        await sleep(backoffDelayMs(attempt));
      }
    }
  }
  throw lastError;
}

/**
 * Upload one batch's parts, up to `MAX_PARALLEL_PARTS` at a time. Defined once, outside any
 * loop, and given everything it touches as a parameter (rather than being declared fresh
 * inside `runDirectUpload`'s batch loop) purely to keep its worker closures away from
 * `no-loop-func` — each call still only runs while the loop that invokes it awaits it, so the
 * two are equivalent in behaviour. Returns this batch's `{part_number, etag}` results.
 */
async function uploadBatch({ videoId, batch, urls, file, partBytes, signal, onPartProgress }) {
  const queue = [...batch];
  const results = [];
  const worker = async () => {
    for (;;) {
      const partNumber = queue.shift();
      if (partNumber === undefined) return;
      const { start, end } = partRange(file, partNumber, partBytes);
      const blob = file.slice(start, end);
      const etag = await uploadOnePartWithRetry({
        videoId,
        partNumber,
        url: urls[String(partNumber)],
        blob,
        signal,
        onProgress: (loaded) => onPartProgress(partNumber, loaded),
      });
      results.push({ part_number: partNumber, etag });
    }
  };
  await Promise.all(Array.from({ length: Math.min(MAX_PARALLEL_PARTS, batch.length) }, worker));
  return results;
}

/**
 * Direct-to-S3 multipart upload for a file at or above `config.upload.direct_upload_min_bytes`
 * (Anshul, 2026-09-26): start the multipart upload, request presigned part URLs in batches of
 * `PARTS_PER_BATCH` — one batch prefetched while the previous one is still uploading, never
 * after it drains (finding C: a batch boundary must not idle the connection on one more
 * request/response round trip) — PUT up to `MAX_PARALLEL_PARTS` of them at once, then
 * complete. `onProgress` receives bytes acknowledged so far, not a percentage, so the caller
 * controls the display.
 */
async function runDirectUpload({ videoId, file, signal, onProgress }) {
  const startResponse = await videosAPI.startDirectUpload(
    videoId,
    { file_size: file.size, content_type: file.type || 'application/octet-stream' },
    { signal }
  );
  const { part_bytes: partBytes, part_count: partCount } = startResponse.data;

  // Per-part loaded bytes, in-flight AND finished, keyed by part number — summed on every
  // update so the bar reflects real bytes acknowledged by S3 at any instant, not just whole
  // parts landing (finding C).
  const partProgress = new Map();
  const reportProgress = (partNumber, loaded) => {
    partProgress.set(partNumber, loaded);
    let total = 0;
    partProgress.forEach((bytes) => {
      total += bytes;
    });
    onProgress(total);
  };

  const batches = [];
  for (let start = 1; start <= partCount; start += PARTS_PER_BATCH) {
    const batch = [];
    for (let n = start; n < start + PARTS_PER_BATCH && n <= partCount; n += 1) batch.push(n);
    batches.push(batch);
  }

  const fetchBatchUrls = (batch) =>
    videosAPI
      .getDirectUploadPartUrls(videoId, { part_numbers: batch }, { signal })
      .then((res) => res.data.urls);

  const completedParts = [];
  let nextUrlsPromise = batches.length ? fetchBatchUrls(batches[0]) : Promise.resolve({});
  for (let i = 0; i < batches.length; i += 1) {
    const batch = batches[i];
    // eslint-disable-next-line no-await-in-loop
    const urls = await nextUrlsPromise;
    // Kick off the NEXT batch's URL request now, concurrently with this batch's part PUTs
    // below, rather than after they all finish (finding C).
    nextUrlsPromise =
      i + 1 < batches.length ? fetchBatchUrls(batches[i + 1]) : Promise.resolve({});

    // eslint-disable-next-line no-await-in-loop
    const batchResults = await uploadBatch({
      videoId,
      batch,
      urls,
      file,
      partBytes,
      signal,
      onPartProgress: reportProgress,
    });
    completedParts.push(...batchResults);
  }

  completedParts.sort((a, b) => a.part_number - b.part_number);
  try {
    await videosAPI.completeDirectUpload(videoId, { parts: completedParts }, { signal });
  } catch (err) {
    if (signal.aborted) throw err;
    // Finding J, 2026-09-26: CloudFront gives its origin 60s to answer `complete`, longer
    // than the client's PREVIOUS 30s timeout on that call (now 120s, but a slow network can
    // still exhaust it) — a timeout here does not mean the upload failed, only that this
    // response never arrived; the server may finish it to READY regardless. Tagged so the
    // caller can treat it as "go check the list" rather than a hard failure.
    const isTimeout = err?.code === 'ECONNABORTED' || /timeout/i.test(err?.message || '');
    if (isTimeout) {
      throw Object.assign(new Error('The upload is still finishing on the server.'), {
        isCompleteTimeoutAmbiguous: true,
      });
    }
    throw err;
  }
}

// The upload journey was measured at 6-7 clicks; one of them was pure friction — the modal
// sat as a fixed overlay over the table after every file finished, so the human had to find
// and click Close before reaching the Start button on the row they just created. The fix:
// once the whole queued batch is done and nothing needs the user's attention (every file
// succeeded — a mid-batch Cancel doesn't count against that), the modal gets out of the way
// on its own instead of waiting to be dismissed. It does NOT self-close on a partial
// failure: an error is exactly the thing that needs the user's attention, so it must stay
// on screen rather than vanish with the rest of the batch. The short delay below (rather
// than closing the instant the last checkmark lands) is so a user who queued several files
// gets a beat to see the full run of green checks — the very results the brief warned could
// otherwise disappear before they are seen — before the modal takes them back to the table.
const AUTO_CLOSE_DELAY_MS = 900;

function extensionOf(name) {
  const dot = name.lastIndexOf('.');
  return dot > 0 && dot < name.length - 1 ? name.slice(dot + 1).toLowerCase() : '';
}

function formatFileSize(bytes) {
  if (bytes === null || bytes === undefined) return '';
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.min(sizes.length - 1, Math.floor(Math.log(bytes) / Math.log(k)));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

/** Extension-then-size validation, in that order, so a wrong-type oversize file reports
 * the more actionable reason first. Returns null when the file is acceptable. `limits` is
 * this component's own derived view of `config.upload` (see `useUploadLimits` below) —
 * never a hard-coded fallback, so this check cannot silently drift from
 * `VideoValidator.validate_file` (backend/app/services/video_validator.py), the real
 * authority a mismatch here only ever costs a slower rejection against, never a bypass. */
function validationError(file, limits) {
  const ext = extensionOf(file.name);
  if (!limits.allowedExtensions.includes(ext)) {
    return `"${file.name}" was not added. Unsupported file type. Allowed: ${limits.allowedLabel}.`;
  }
  if (file.size > limits.maxBytes) {
    return `"${file.name}" was not added. Files must be ${limits.maxLabel} or smaller.`;
  }
  return null;
}

/** Derives this component's limits and labels from `usePublicConfig()`'s `config.upload`
 * (AWS_DEPLOYMENT_PLAN.md C4) — `null` until the config has loaded, so a caller renders
 * nothing that depends on a guessed default (module docstring's own reasoning). */
function useUploadLimits() {
  const { config } = usePublicConfig();
  const upload = config?.upload;
  if (!upload) return null;
  return {
    allowedExtensions: upload.video_allowed_file_types,
    allowedLabel: upload.video_allowed_file_types.map((ext) => ext.toUpperCase()).join(', '),
    acceptAttribute: upload.video_allowed_file_types.map((ext) => `.${ext}`).join(','),
    maxBytes: upload.video_max_file_size,
    maxLabel: formatFileSize(upload.video_max_file_size),
    maxDurationHours: Math.round(upload.video_max_duration_s / 3600),
    directUploadEnabled: upload.direct_upload_enabled,
    directUploadMinBytes: upload.direct_upload_min_bytes,
  };
}

/**
 * Upload one or more walkthrough videos into a project (plan 16.2, 16.4).
 *
 * Two-phase flow per file: `prepare-upload` creates the DB row, then either `upload-file`
 * (small files: streams the bytes with a real `onUploadProgress`-driven bar) or the direct
 * browser-to-S3 multipart flow (large files, Anshul 2026-09-26: `runDirectUpload` above) —
 * chosen per file against `config.upload.direct_upload_min_bytes`, never a client-side
 * guess. Native drag events, not react-dropzone (not a dependency of this client — see
 * ImportStandardPage.js, which this component's drag/drop handlers mirror), because a
 * multi-file video uploader still needs only "drag files here or click to pick", nothing
 * react-dropzone adds beyond that for a single-purpose input.
 */
export default function VideoUpload({ isOpen, projectId, onClose }) {
  const queryClient = useQueryClient();
  const uploadLimits = useUploadLimits();
  const [rows, setRows] = useState([]);
  const [isDragActive, setIsDragActive] = useState(false);
  const fileInputRef = useRef(null);
  const nextIdRef = useRef(0);

  const updateRow = useCallback((id, patch) => {
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  }, []);

  const runUpload = useCallback(
    async (rowId, file) => {
      const controller = new AbortController();
      updateRow(rowId, { controller, status: 'preparing' });
      // Hoisted out of the `try` (finding A, 2026-09-26): the `catch` below needs to know
      // whether THIS upload was a direct-to-S3 one, and its video id, to abort the
      // multipart upload on a failure -- neither is available to a `catch` block from a
      // `const` declared inside the `try` it guards.
      let videoId;
      let useDirect = false;

      try {
        const prepared = await videosAPI.prepareUpload(
          projectId,
          { original_filename: file.name },
          { signal: controller.signal }
        );
        // Contract (plan 16.2) is `{video}`; tolerate an unwrapped object too so this
        // component keeps working if that shape firms up differently once the backend
        // route lands.
        const video = prepared.data?.video || prepared.data;
        videoId = video?.id;
        if (!videoId) {
          throw new Error('The server did not return a video id for this upload');
        }

        useDirect =
          uploadLimits.directUploadEnabled && file.size >= uploadLimits.directUploadMinBytes;
        updateRow(rowId, { status: 'uploading', progress: 0, videoId, isDirect: useDirect });

        if (useDirect) {
          await runDirectUpload({
            videoId,
            file,
            signal: controller.signal,
            onProgress: (acknowledgedBytes) => {
              const pct = Math.round((acknowledgedBytes * 100) / file.size);
              updateRow(rowId, { progress: pct });
            },
          });
        } else {
          const formData = new FormData();
          formData.append('file', file);
          await videosAPI.uploadFileForVideo(
            videoId,
            formData,
            (progressEvent) => {
              if (!progressEvent?.total) return;
              const pct = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              updateRow(rowId, { progress: pct });
            },
            { signal: controller.signal }
          );
        }

        updateRow(rowId, { status: 'success', progress: 100 });
        queryClient.invalidateQueries(['project-videos', projectId]);
      } catch (err) {
        if (controller.signal.aborted) {
          updateRow(rowId, { status: 'canceled' });
          return;
        }
        if (err?.isCompleteTimeoutAmbiguous) {
          // Finding J, 2026-09-26: the client's own `complete` request timed out, but the
          // server may still finish the upload to READY -- neither a hard failure message
          // nor an abort (which would cancel a multipart upload S3 may already have
          // completed) is correct here. Leave the row for the list refetch below to settle.
          updateRow(rowId, {
            status: 'verifying',
            error: 'Still finishing on the server — check the list in a moment.',
          });
          queryClient.invalidateQueries(['project-videos', projectId]);
          return;
        }
        // Finding A, 2026-09-26: a direct upload that fails for any reason OTHER than the
        // user's own Cancel (a missing CORS rule, a part that exhausted its retries, a
        // dropped connection) must not leave sibling part workers running against a bucket
        // this row has given up on, nor bill S3 for parts nothing will ever complete.
        // `controller.abort()` stops every in-flight PUT sharing this controller's signal
        // (`Promise.all` in `uploadBatch` does not do that on its own); `abortDirectUpload`
        // is best-effort, same reasoning as the user's own Cancel already documents.
        if (useDirect && videoId) {
          controller.abort();
          videosAPI.abortDirectUpload(videoId).catch(() => {});
        }
        updateRow(rowId, { status: 'error', error: getApiErrorMessage(err, 'Upload failed') });
      }
    },
    [projectId, queryClient, updateRow, uploadLimits]
  );

  const addFiles = useCallback(
    (fileList) => {
      const incoming = Array.from(fileList || []);
      if (!incoming.length || !uploadLimits) return;

      const rejectionMessages = [];

      incoming.forEach((file) => {
        const reason = validationError(file, uploadLimits);
        const id = `row-${nextIdRef.current++}`;
        if (reason) {
          rejectionMessages.push(reason);
          setRows((prev) => [
            ...prev,
            { id, name: file.name, size: file.size, status: 'rejected', error: reason },
          ]);
          return;
        }
        setRows((prev) => [
          ...prev,
          { id, name: file.name, size: file.size, status: 'pending', progress: 0 },
        ]);
        runUpload(id, file);
      });

      if (rejectionMessages.length) {
        toast.error(rejectionMessages.join(' '), { id: 'upload-file-validation', duration: 6000 });
      }
    },
    [runUpload, uploadLimits]
  );

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setIsDragActive(false);
      if (e.dataTransfer?.files?.length) addFiles(e.dataTransfer.files);
    },
    [addFiles]
  );

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragActive(false);
  };

  const handleFileInputChange = (e) => {
    addFiles(e.target.files);
    e.target.value = '';
  };

  const handleCancel = useCallback((rowId) => {
    setRows((prev) => {
      const row = prev.find((r) => r.id === rowId);
      // Stops every in-flight request first (both the axios calls and, for a direct
      // upload, the browser's own `window.fetch` part PUTs share this controller's signal).
      row?.controller?.abort();
      if (row?.isDirect && row?.videoId) {
        // Fire-and-forget: cancel must always take effect locally even if this never
        // reaches the server (the video row already moved on from the client's point of
        // view); a stray in-progress multipart upload only costs storage, not correctness.
        videosAPI.abortDirectUpload(row.videoId).catch(() => {});
      }
      return prev;
    });
  }, []);

  const handleClose = useCallback(() => {
    setRows([]);
    onClose?.();
  }, [onClose]);

  // Fires once per completed, all-success batch (see AUTO_CLOSE_DELAY_MS above for why this
  // waits instead of closing on the spot, and why a partial failure is excluded entirely).
  useEffect(() => {
    if (!isOpen || rows.length === 0) return undefined;

    const stillBusy = rows.some((row) => row.status === 'preparing' || row.status === 'uploading');
    const hasFailure = rows.some((row) => row.status === 'error' || row.status === 'rejected');
    const hasSuccess = rows.some((row) => row.status === 'success');
    if (stillBusy || hasFailure || !hasSuccess) return undefined;

    const timeoutId = setTimeout(handleClose, AUTO_CLOSE_DELAY_MS);
    return () => clearTimeout(timeoutId);
  }, [rows, isOpen, handleClose]);

  if (!isOpen) return null;

  const isBusy = rows.some((row) => row.status === 'preparing' || row.status === 'uploading');

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 transition-opacity" onClick={handleClose} />

        <div className="relative inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Upload videos</h3>
            <button
              type="button"
              onClick={handleClose}
              className="rounded-md text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {uploadLimits ? (
            <div
              onClick={() => fileInputRef.current?.click()}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              role="button"
              tabIndex={0}
              className={`upload-area cursor-pointer ${isDragActive ? 'dragover' : ''}`}
            >
              {/* `accept` narrows the OS picker's default filter but does not hide a
                  mistaken .avi from a user who chooses "All Files" — it still reaches
                  `addFiles`, which shows our own rejection message rather than silently
                  discarding it (DocumentUpload.js's documented reasoning for the same
                  choice about a bare accept attribute). */}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={uploadLimits.acceptAttribute}
                onChange={handleFileInputChange}
                className="hidden"
                aria-label="Upload videos"
              />
              <CloudArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
              <p className="mt-2 text-sm text-gray-600">
                Drag &amp; drop walkthrough videos here, or click to select files
              </p>
              <p className="mt-1 text-xs text-gray-400">
                Supports {uploadLimits.allowedLabel} (max {uploadLimits.maxLabel} and{' '}
                {uploadLimits.maxDurationHours} hours each)
              </p>
            </div>
          ) : (
            <div className="upload-area py-10 text-center text-sm text-gray-500">
              Loading upload settings…
            </div>
          )}

          {rows.length > 0 && (
            <ul className="mt-4 space-y-3 max-h-64 overflow-y-auto">
              {rows.map((row) => (
                <li key={row.id} className="text-sm">
                  <div className="flex items-center gap-2 text-gray-600 min-w-0">
                    <FilmIcon className="h-4 w-4 flex-shrink-0 text-gray-400" />
                    <span className="truncate flex-1 min-w-0">{row.name}</span>
                    <span className="text-xs text-gray-400 flex-shrink-0">{formatFileSize(row.size)}</span>
                    {row.status === 'success' && (
                      <CheckCircleIcon className="h-5 w-5 text-green-500 flex-shrink-0" aria-label="Uploaded" />
                    )}
                    {(row.status === 'error' || row.status === 'rejected') && (
                      <ExclamationCircleIcon className="h-5 w-5 text-red-500 flex-shrink-0" aria-label="Failed" />
                    )}
                    {(row.status === 'preparing' || row.status === 'uploading') && (
                      <button
                        type="button"
                        onClick={() => handleCancel(row.id)}
                        className="text-xs text-gray-400 hover:text-red-600 flex-shrink-0"
                      >
                        Cancel
                      </button>
                    )}
                  </div>

                  {row.status === 'uploading' && (
                    <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                      <div
                        className="bg-blue-500 h-2 rounded-full transition-all"
                        style={{ width: `${row.progress || 0}%` }}
                        role="progressbar"
                        aria-valuenow={row.progress || 0}
                        aria-valuemin={0}
                        aria-valuemax={100}
                      />
                    </div>
                  )}
                  {row.status === 'preparing' && (
                    <p className="mt-1 text-xs text-gray-400">Preparing upload…</p>
                  )}
                  {row.status === 'canceled' && <p className="mt-1 text-xs text-gray-500">Canceled</p>}
                  {row.status === 'verifying' && (
                    <p className="mt-1 text-xs text-gray-500">{row.error}</p>
                  )}
                  {(row.status === 'error' || row.status === 'rejected') && row.error && (
                    <p role="alert" className="mt-1 text-xs text-red-600">
                      {row.error}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          )}

          <div className="flex justify-end pt-4 mt-2">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-[#F8F8FF] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {isBusy ? 'Upload in background' : 'Close'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
